/* ─────────────────────────────────────────────────────────────────────────────
 * CreateRequestDialog — in-place creation popup for the Approval Center.
 *
 * Renders the chosen request type's `create` descriptor (see requestRegistry.js)
 * as a compact, professional form and submits through that type's own `submit()`
 * — reusing the exact same APIs/payloads the dedicated pages use. On success the
 * parent reloads the list so the new request appears immediately.
 * ──────────────────────────────────────────────────────────────────────────── */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, IconButton,
  TextField, MenuItem, Button, CircularProgress, Collapse, LinearProgress, Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WorkerPicker from '../common/WorkerPicker';
import { TYPE_BY_ID } from './requestRegistry';

const T = {
  brand: '#3D8FA3', ink: '#0F172A', ink700: '#334155', ink500: '#64748B', ink400: '#94A3B8',
  line: '#E2E8F0', surface: '#F8FAFC', white: '#FFFFFF',
};

/** 'YYYY-MM-DD' → '01.09.2026'. Datat e lehonisë janë ditë kalendarike, pa orë. */
const dmy = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (v || '');
};

/** Kush e paguan secilën periudhë — vjen si kod nga shërbimi. */
const PAYER_LABEL = { employer: 'Punëdhënësi', government: 'Qeveria', none: 'Pa pagesë' };

export default function CreateRequestDialog({ open, type, title, ctx, workers = [], onClose, onCreated }) {
  // The general-request dialog can toggle into "Korrigjim prezence" mode, which
  // swaps in the attendance-correction create config (fields, submit, colour).
  const canToggleCorrection = type?.id === 'general_request' && !!TYPE_BY_ID.attendance_correction?.create;
  const [mode, setMode] = useState('general'); // 'general' | 'correction'
  const effType = (canToggleCorrection && mode === 'correction') ? TYPE_BY_ID.attendance_correction : type;
  const create = effType?.create;
  const accent = effType?.color || T.brand;

  const [values, setValues] = useState({});
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(false);
  /**
   * Opsione që vijnë nga serveri — projektet, p.sh.
   *
   * `options` ishte vetëm listë e ngurtë. Për projektet ajo s'mjafton: ato
   * ndryshojnë, dhe një listë e shkruar në kod do të dilte jashtë sinkronit që
   * te projekti i dytë. Lexohen një herë kur hapet dialogu, jo për çdo shkronjë.
   */
  const [asyncOptions, setAsyncOptions] = useState({});

  // Start every open on the primary (general) mode.
  useEffect(() => { if (open) setMode('general'); }, [open, type?.id]);

  // Reset the form whenever the active config changes (new type or mode switch).
  useEffect(() => {
    if (open && create) {
      setValues(create.initial ? create.initial(ctx) : {});
      setTouched({});
      setError('');
      setInfo(null);
    }
  }, [open, effType?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) { setAsyncOptions({}); return undefined; }
    let alive = true;
    const loaders = (create?.fields || []).filter((f) => typeof f.loadOptions === 'function');
    if (!loaders.length) return undefined;
    Promise.all(loaders.map(async (f) => [f.name, await f.loadOptions().catch(() => [])]))
      .then((pairs) => {
        if (!alive) return;
        setAsyncOptions(Object.fromEntries(pairs));
        // Kur përdoruesi ka VETËM një projekt të vetin, ajo është përgjigjja e
        // pritur: propozohet e gatshme. Me disa, asnjëra nuk hamendësohet —
        // një zgjedhje e gabuar e heshtur është më keq se një fushë bosh.
        for (const [name, list] of pairs) {
          const mine = (list || []).filter((o) => o.mine);
          if (mine.length === 1) setValues((v) => (v[name] ? v : { ...v, [name]: mine[0].value }));
        }
      });
    return () => { alive = false; };
  }, [open, create]);

  // Optional async info panel (e.g. leave balance) — recomputed when the fields
  // listed in `create.infoKeys` change.
  const infoSig = (create?.infoKeys || []).map((k) => values[k]).join('|');
  useEffect(() => {
    if (!open || !create?.loadInfo) return undefined;
    let alive = true;
    setInfoLoading(true);
    // Debounced: `infoKeys` now includes typed fields (dates, hours) and this
    // panel may reach the server, so a burst of keystrokes must not become a
    // burst of requests. The last one wins.
    const timer = setTimeout(() => {
      Promise.resolve(create.loadInfo(values, ctx))
        .then((res) => { if (alive) setInfo(res || null); })
        .catch(() => { if (alive) setInfo(null); })
        .finally(() => { if (alive) setInfoLoading(false); });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [open, infoSig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fusha që përshkruesi i llogarit vetë (p.sh. fundi i lehonisë, 12 muaj pas
  // fillimit). Zbatohen VETËM kur fusha është bosh: një vlerë e shkruar me dorë
  // nuk mbishkruhet kurrë, përndryshe forma do t'i luftonte gishtat.
  // Çdo vlerë vendoset vetëm NJË herë NË NJË FUSHË BOSH. Pa këtë, fshirja e
  // datës nga përdoruesi do të rimbushej menjëherë nga e njëjta vlerë — forma
  // do t'i luftonte gishtat.
  //
  // Përjashtimi i vetëm: kur fusha ende mban një vlerë që e ka shkruar VETË ky
  // dialog, ajo rifreskohet kur burimi i saj ndryshon. Ndryshe, zgjedhja "3
  // muaj pa pagesë" te lehonia do ta zhvendoste fundin e periudhës në panel
  // dhe data e mbarimit do të mbetej ajo e vjetra — dy të vërteta në një ekran.
  // Çfarëdo që ka shkruar njeriu mbetet e paprekur.
  const autofilled = useRef(new Map());   // fusha → vlerat që i ka vënë dialogu
  useEffect(() => { if (open) autofilled.current = new Map(); }, [open, effType?.id]);

  useEffect(() => {
    if (!open || !create?.autofill) return;
    const patchObj = create.autofill(values, info);
    if (!patchObj) return;
    const mine = (k) => autofilled.current.get(k) || new Set();
    const pending = Object.entries(patchObj).filter(([k, v]) => {
      if (v == null || v === '' || values[k] === v) return false;
      const empty = values[k] === '' || values[k] == null;
      if (empty) return !mine(k).has(v);          // e fshirë me dorë → mos e rimbush
      return mine(k).has(values[k]);              // e vendosur nga dialogu → ndiqe
    });
    if (!pending.length) return;
    pending.forEach(([k, v]) => {
      const set = autofilled.current.get(k) || new Set();
      set.add(v);
      autofilled.current.set(k, set);
    });
    setValues((p) => ({ ...p, ...Object.fromEntries(pending) }));
  }, [open, info, values, create]); // eslint-disable-line react-hooks/exhaustive-deps

  const fields = useMemo(
    () => (create?.fields || [])
      .filter((f) => (typeof f.when === 'function' ? f.when(ctx || {}) : true))
      .filter((f) => (typeof f.showIf === 'function' ? f.showIf(values) : true)),
    [create, ctx, values],
  );

  const isEmpty = (v) => v === '' || v == null;
  const summary = create?.summary ? create.summary(values) : '';
  const formAlert = create?.alert ? create.alert(values, info) : null;
  const firstInvalid = fields.find((f) => f.required && isEmpty(values[f.name]));

  // Cross-field rules (end before start, half-day across two dates, …). They run
  // on every keystroke instead of at submit: a form that accepts input and then
  // rejects it on send makes the user guess which field was wrong. Each rule is
  // written to stay silent until the fields it judges are actually filled.
  const violation = create?.validate ? create.validate(values, info) : null;

  if (!type || !create) return null;

  const set = (name, v) => { setValues((p) => ({ ...p, [name]: v })); if (error) setError(''); };
  const markTouched = (name) => setTouched((p) => ({ ...p, [name]: true }));

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px', bgcolor: T.white, fontSize: 13.5,
      '& fieldset': { borderColor: T.line },
      '&:hover fieldset': { borderColor: alpha(accent, 0.5) },
      '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 1 },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(accent, 0.12)}` },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
  };

  const handleSubmit = async () => {
    if (firstInvalid) {
      setTouched(Object.fromEntries(fields.map((f) => [f.name, true])));
      setError('Plotëso fushat e detyrueshme të shënuara me *.');
      return;
    }
    if (violation) { setError(violation.text); return; }
    setBusy(true); setError('');
    const r = await create.submit(values, ctx);
    setBusy(false);
    if (!r?.ok) { setError(r?.error || 'Ndodhi një gabim gjatë dërgimit.'); return; }
    onCreated?.(r.data);
  };

  const renderField = (f) => {
    const showErr = !!touched[f.name] && f.required && isEmpty(values[f.name]);
    const helpText = typeof f.help === 'function' ? f.help(values, info) : f.help;

    // File upload (e.g. medical certificate) — stores the File object in values.
    if (f.kind === 'file') {
      const file = values[f.name];
      // Etiketa mund të varet nga forma: i njëjti kuti ngarkimi kërkon raport
      // shtatzënie para lindjes dhe certifikatë lindjeje pas saj.
      const label = typeof f.label === 'function' ? f.label(values) : f.label;
      const placeholder = typeof f.placeholder === 'function' ? f.placeholder(values) : f.placeholder;
      return (
        <Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: T.ink500, mb: 0.5 }}>
            {label}{f.required ? ' *' : ''}
          </Typography>
          <Button
            component="label" fullWidth disableElevation
            variant="outlined"
            startIcon={file
              ? <CheckCircleRoundedIcon sx={{ fontSize: 17 }} />
              : <AttachFileRoundedIcon sx={{ fontSize: 17 }} />}
            sx={{
              justifyContent: 'flex-start', textTransform: 'none', borderRadius: '10px',
              borderStyle: 'dashed', borderWidth: 1.5, py: 1, px: 1.25,
              borderColor: file ? accent : T.line, color: file ? accent : T.ink500,
              bgcolor: T.white, fontSize: 13, fontWeight: 600, overflow: 'hidden',
              '& .MuiButton-startIcon': { mr: 1 },
              '&:hover': { borderColor: accent, bgcolor: alpha(accent, 0.04) },
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file ? file.name : (placeholder || 'Zgjidh dokument')}
            </Box>
            <input
              type="file" hidden accept={f.accept}
              onChange={(e) => {
                const x = e.target.files?.[0] || null;
                markTouched(f.name);
                if (!x) return;
                if (x.size > 5 * 1024 * 1024) { setError('Skedari nuk duhet të kalojë 5 MB.'); return; }
                set(f.name, x);
              }}
            />
          </Button>
          {helpText ? (
            <Typography sx={{ fontSize: '0.68rem', mt: 0.5, color: T.ink400 }}>{helpText}</Typography>
          ) : null}
        </Box>
      );
    }

    const common = {
      fullWidth: true, size: 'small', label: f.label, required: f.required,
      value: values[f.name] ?? '', error: showErr,
      helperText: showErr ? 'Kjo fushë është e detyrueshme.' : (helpText || ' '),
      onBlur: () => markTouched(f.name),
      sx: fieldSx,
      InputLabelProps: f.kind === 'date' || f.kind === 'time' ? { shrink: true } : undefined,
    };
    // Employee — searchable combobox, never a list of the whole company.
    if (f.kind === 'worker') {
      // Disa lloje kërkesash nuk i takojnë të gjithëve — lehonia është e
      // punonjëses. Filtri jeton te përshkrimi i fushës, jo këtu: ky ekran nuk
      // di asgjë për llojet e pushimit, dhe s'duhet të mësojë.
      //
      // `note` zë vendin e ndihmës vetëm kur filtri ka çfarë të shpjegojë; pa
      // të, dikush që mungon nga lista do të dukej sikur nuk ekziston.
      const wf = typeof f.filterWorkers === 'function' ? f.filterWorkers(workers, values) : null;
      return (
        <WorkerPicker
          workers={wf ? wf.list : workers}
          value={values[f.name] ?? ''}
          onChange={(v) => { markTouched(f.name); set(f.name, v); }}
          onBlur={() => markTouched(f.name)}
          label={f.label} required={f.required} error={showErr}
          helperText={wf?.note || common.helperText}
          accent={accent} sx={fieldSx}
        />
      );
    }
    /**
     * Zgjedhës i kërkueshëm, me grupim — për lista që rriten.
     *
     * Një `Select` me tridhjetë projekte brenda kërkon shfletim me sy. Ky
     * shkruhet: dy shkronja e ngushtojnë listën. Dhe projektet e vetë
     * përdoruesit rrinë lart, sepse ato janë ato që kërkon nëntëdhjetë për qind
     * e rasteve.
     */
    if (f.kind === 'combo') {
      const opts = f.loadOptions ? (asyncOptions[f.name] || []) : (f.options || []);
      const selected = opts.find((o) => String(o.value) === String(values[f.name] ?? '')) || null;
      const grouped = opts.some((o) => o.mine);
      return (
        <Autocomplete
          options={opts}
          value={selected}
          onChange={(_, o) => { markTouched(f.name); set(f.name, o ? o.value : ''); }}
          getOptionLabel={(o) => o?.label || ''}
          isOptionEqualToValue={(o, v) => String(o.value) === String(v?.value)}
          groupBy={grouped ? (o) => (o.mine ? 'Projektet e mia' : 'Të tjerat') : undefined}
          size="small" fullWidth openOnFocus autoHighlight blurOnSelect
          noOptionsText={f.loadOptions && !opts.length ? 'Po lexohen…' : 'Asnjë përputhje'}
          sx={fieldSx}
          renderInput={(params) => (
            <TextField {...params} label={f.label} required={f.required}
              helperText={helpText || ' '} placeholder={f.placeholder} />
          )}
        />
      );
    }
    if (f.kind === 'select') {
      const opts = f.loadOptions ? (asyncOptions[f.name] || []) : (f.options || []);
      return (
        <TextField {...common} select onChange={(e) => set(f.name, e.target.value)}>
          {/* Një zgjedhës i zbrazët duket i prishur; kur lista ende s'ka ardhur
              ose është bosh, thuhet hapur. */}
          {opts.length === 0 && (
            <MenuItem value="" disabled>{f.loadOptions ? 'Po lexohen…' : '—'}</MenuItem>
          )}
          {f.allowEmpty && <MenuItem value="">—</MenuItem>}
          {opts.map((o) => <MenuItem key={String(o.value)} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      );
    }
    // Bounds the browser can enforce itself: `min`/`max` on a date input grey
    // out the impossible days in the native picker, so the end-before-start
    // mistake is prevented rather than reported. `bounds` reads other values,
    // which is why it is a function on the field, not a constant.
    const bounds = typeof f.bounds === 'function' ? (f.bounds(values) || {}) : {};

    return (
      <TextField
        {...common}
        type={f.kind === 'date' ? 'date' : f.kind === 'time' ? 'time' : f.kind === 'number' ? 'number' : 'text'}
        multiline={f.kind === 'textarea'} minRows={f.kind === 'textarea' ? 2.5 : undefined}
        placeholder={f.placeholder}
        inputProps={{ ...bounds }}
        onChange={(e) => set(f.name, e.target.value)}
      />
    );
  };

  const TypeIcon = effType?.Icon || type.Icon;
  const headerTitle = canToggleCorrection
    ? (mode === 'correction' ? 'Korrigjim prezence' : 'Kërkesë e përgjithshme')
    : (title || 'Kërkesë e re');

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden', boxShadow: '0 24px 64px rgba(15,23,42,.22)' } }}>
      {/* Clean, corporate header — soft accent tint fading to white */}
      <Box sx={{
        position: 'relative', px: 2.75, pt: 2.5, pb: 2,
        background: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, ${T.white} 100%)`,
        borderBottom: `1px solid ${T.line}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px', bgcolor: accent, color: T.white, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 6px 14px ${alpha(accent, 0.30)}`,
          }}>
            {TypeIcon ? <TypeIcon sx={{ fontSize: 22 }} /> : null}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.64rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.ink400 }}>
              Kërkesë e re
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: T.ink, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
              {headerTitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={busy} size="small"
            sx={{ color: T.ink400, '&:hover': { bgcolor: alpha(T.ink, 0.06), color: T.ink } }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: 2.75, py: '18px !important', bgcolor: T.surface }}>
        {/* Mode switch: general request ⇄ attendance correction */}
        {canToggleCorrection && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.75 }}>
            {[
              { key: 'general', label: 'Kërkesë e përgjithshme', Icon: type.Icon, color: type.color || T.brand },
              { key: 'correction', label: 'Korrigjim prezence', Icon: TYPE_BY_ID.attendance_correction.Icon, color: TYPE_BY_ID.attendance_correction.color || T.brand },
            ].map((m) => {
              const on = mode === m.key;
              const MIcon = m.Icon;
              return (
                <Box
                  key={m.key} onClick={() => !busy && setMode(m.key)}
                  sx={{
                    flex: 1, cursor: busy ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 0.875,
                    px: 1.25, py: 1, borderRadius: '12px', minWidth: 0,
                    border: `1.5px solid ${on ? m.color : T.line}`,
                    bgcolor: on ? alpha(m.color, 0.08) : T.white, transition: 'all .12s',
                    '&:hover': busy ? {} : { borderColor: m.color },
                  }}
                >
                  <Box sx={{ width: 30, height: 30, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: on ? m.color : alpha(m.color, 0.12), color: on ? '#fff' : m.color }}>
                    {MIcon ? <MIcon sx={{ fontSize: 17 }} /> : null}
                  </Box>
                  <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 700, color: on ? m.color : T.ink700, lineHeight: 1.15 }}>{m.label}</Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Optional balance panel (e.g. remaining leave days) */}
        {(info || infoLoading) && (
          <Box sx={{
            mb: 1.5, px: 1.5, py: 1.125, borderRadius: '12px',
            bgcolor: alpha(accent, 0.07), border: `1px solid ${alpha(accent, 0.2)}`,
          }}>
            {infoLoading && !info ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} sx={{ color: accent }} />
                <Typography sx={{ fontSize: '0.78rem', color: T.ink500 }}>Duke ngarkuar balancën…</Typography>
              </Box>
            ) : info ? (() => {
              const unlimited = info.allowance === Infinity || info.allowance == null;
              const pct = unlimited ? 0 : Math.min(100, Math.round((info.used / Math.max(1, info.allowance)) * 100));
              const barColor = unlimited ? accent : pct >= 90 ? '#DC2626' : pct >= 65 ? '#D97706' : '#16A34A';
              return (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.625 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: T.ink }}>{info.label}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: barColor }}>
                      {unlimited
                        ? 'Pa kufi'
                        /* `exact` = the figure came from the server, so it already
                           carries last year's carry-over, the seniority bonus and
                           any HR correction. It can legitimately exceed the base
                           allowance, and "22.4 / 20" would read like a bug. */
                        : info.exact ? `${info.remaining} ${info.unit === 'hours' ? 'orë' : 'ditë'} të mbetura`
                          : `${info.remaining} / ${info.allowance} ${info.unit === 'hours' ? 'orë' : 'ditë'}`}
                    </Typography>
                  </Box>
                  {!unlimited && (
                    <LinearProgress
                      variant="determinate" value={pct}
                      sx={{ height: 6, borderRadius: 4, bgcolor: alpha(accent, 0.14), '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 4 } }}
                    />
                  )}
                  <Typography sx={{ mt: 0.5, fontSize: '0.7rem', color: T.ink500 }}>
                    {`Përdorur ${info.used} ditë këtë vit`}
                    {info.exact && info.allowance != null && info.remaining > info.allowance
                      ? `  ·  përfshin ditë të bartura nga viti i kaluar`
                      : ''}
                  </Typography>
                </>
              );
            })() : null}
          </Box>
        )}
        {/* ── Periudhat e lehonisë ────────────────────────────────────────────
            6 muaj të paguar + 3, dhe sipas zgjedhjes edhe 3 muaj pa pagesë — të
            llogaritura nga backend/services/maternityLeave.js me normat e
            cilësimeve. Asnjë shifër këtu nuk vjen nga browser-i. */}
        {info?.maternity?.periods?.length > 0 && (
          <Box sx={{
            mb: 1.5, borderRadius: '12px', overflow: 'hidden',
            border: `1px solid ${alpha('#DB2777', 0.25)}`, bgcolor: alpha('#DB2777', 0.04),
          }}>
            <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#9D174D' }}>
                Periudhat e lehonisë
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: T.ink500 }}>
                {info.maternity.totalMonths} muaj · {info.maternity.totalDays} ditë
              </Typography>
            </Box>

            {info.maternity.periods.map((p) => (
              <Box key={p.key} sx={{
                px: 1.5, py: 0.875, display: 'flex', alignItems: 'center', gap: 1,
                borderTop: `1px solid ${alpha('#DB2777', 0.14)}`,
              }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontSize: '0.76rem', fontWeight: 700, color: T.ink }}>
                    {p.label}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: '0.7rem', color: T.ink500 }}>
                    {dmy(p.startDate)} – {dmy(p.endDate)}  ·  {p.months} muaj
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: p.ratePercent > 0 ? '#15803D' : T.ink400, whiteSpace: 'nowrap' }}>
                  {p.ratePercent}%
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: T.ink500, minWidth: 74, textAlign: 'right' }}>
                  {PAYER_LABEL[p.payer] || ''}
                </Typography>
              </Box>
            ))}

            <Box sx={{ px: 1.5, py: 0.875, borderTop: `1px solid ${alpha('#DB2777', 0.14)}` }}>
              <Typography sx={{ fontSize: '0.68rem', color: T.ink500 }}>
                {`Data e mbarimit u plotësua me fundin e periudhës ${info.maternity.totalMonths}-mujore`}
                {info.maternity.includesUnpaid === false
                  ? ' · pa fazën 3-mujore pa pagesë' : ''}
                {info.maternity.crossesLeapDay ? ' · përfshin 29 shkurtin' : ''}
                {info.maternity.periods.some((p) => p.dayClamped)
                  ? ' · dita e muajit u afrua te fundi i muajit më të shkurtër' : ''}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Contextual notice (e.g. mandatory medical certificate) */}
        {formAlert && (
          <Box sx={{
            mb: 1.5, px: 1.5, py: 1, borderRadius: '12px', display: 'flex', gap: 1, alignItems: 'flex-start',
            bgcolor: formAlert.tone === 'error' ? '#FEF2F2' : '#FFF7ED',
            border: `1px solid ${formAlert.tone === 'error' ? '#FECACA' : '#FED7AA'}`,
          }}>
            <ErrorOutlineRoundedIcon sx={{ fontSize: 17, mt: '1px', flexShrink: 0, color: formAlert.tone === 'error' ? '#DC2626' : '#EA580C' }} />
            <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, lineHeight: 1.4, color: formAlert.tone === 'error' ? '#B91C1C' : '#9A3412' }}>
              {formAlert.text}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5 }}>
          {fields.map((f) => (
            <Box key={f.name} sx={{ flex: f.half ? '1 1 calc(50% - 6px)' : '1 1 100%', minWidth: f.half ? 150 : '100%' }}>
              {renderField(f)}
            </Box>
          ))}
        </Box>

        {/* A rule the form itself can decide — shown while typing, and the send
            button stays out of reach until it is resolved. */}
        <Collapse in={!!violation && !error}>
          <Box sx={{
            mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
            borderRadius: '10px', bgcolor: '#FEF2F2', border: '1px solid #FECACA',
          }}>
            <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: '#DC2626' }} />
            <Typography sx={{ fontSize: '0.82rem', color: '#B91C1C', fontWeight: 600 }}>
              {violation?.text}
            </Typography>
          </Box>
        </Collapse>

        <Collapse in={!!error}>
          <Box sx={{
            mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
            borderRadius: '10px', bgcolor: '#FEF2F2', border: '1px solid #FECACA',
          }}>
            <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: '#DC2626' }} />
            <Typography sx={{ fontSize: '0.82rem', color: '#B91C1C', fontWeight: 600 }}>{error}</Typography>
          </Box>
        </Collapse>
      </DialogContent>

      <DialogActions sx={{ px: 2.75, py: 1.75, gap: 1, borderTop: `1px solid ${T.line}`, justifyContent: 'space-between' }}>
        {/* Live recap chip (e.g. working days for leave) */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {summary ? (
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.625, px: 1.125, py: 0.5,
              borderRadius: '8px', bgcolor: alpha(accent, 0.1), color: accent,
            }}>
              <AutoAwesomeRoundedIcon sx={{ fontSize: 15 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{summary}</Typography>
            </Box>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button onClick={onClose} disabled={busy}
            sx={{ textTransform: 'none', fontWeight: 700, color: T.ink500, borderRadius: '10px', px: 1.75 }}>
            Anulo
          </Button>
          <Button
            variant="contained" disableElevation onClick={handleSubmit} disabled={busy || !!violation}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: accent, fontWeight: 700, borderRadius: '10px', textTransform: 'none', px: 2.5,
              boxShadow: `0 6px 16px ${alpha(accent, 0.28)}`,
              '&:hover': { bgcolor: accent, filter: 'brightness(0.93)' },
            }}
          >
            {busy ? 'Duke dërguar…' : 'Dërgo kërkesën'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
